import type { UpdateMutationResult } from "@/customTypings/api/UpdateMutation";
import type {
    DisposalRecoveryCodeCreatePayload,
    DisposalRecoveryCodeUpdatePayload,
    IDisposalRecoveryCodeDetail,
} from "@/customTypings/reference/DisposalRecoveryCode";
import { DR_CREATE_CONFLICT } from "@/customTypings/reference/DisposalRecoveryCode";
import type { ReferenceRestoreDeletedPayload } from "@/utils/reference/referenceCreateConflict";
import { authHeader, handleResponse } from "../../utils";
import { appendIncludeAuditQuery, type DetailGetByIdOptions } from "@/utils/api/detailGetOptions";
import { postReferenceCreate } from "@/utils/reference/referenceCreateConflict";

export const disposalRecoveryCodesService = {
    getById,
    create,
    restoreDeletedWithCreateValues,
    update,
    disable,
    enable,
    softDelete,
    restore,
};

function getById(id: string, options?: DetailGetByIdOptions) {
    const requestOptions: RequestInit = {
        method: "GET",
        headers: authHeader(),
        credentials: "include",
    };
    return fetch(
        appendIncludeAuditQuery(
            `/api/referencedata/disposal-recovery-codes/${encodeURIComponent(id)}`,
            options
        ),
        requestOptions
    ).then(handleResponse) as Promise<IDisposalRecoveryCodeDetail>;
}

function create(payload: DisposalRecoveryCodeCreatePayload) {
    return postReferenceCreate<IDisposalRecoveryCodeDetail>(
        `/api/referencedata/disposal-recovery-codes/create`,
        payload,
        DR_CREATE_CONFLICT.deletedRestoreAvailable
    );
}

function restoreDeletedWithCreateValues(payload: ReferenceRestoreDeletedPayload) {
    const requestOptions: RequestInit = {
        method: "POST",
        headers: authHeader(),
        credentials: "include",
        body: JSON.stringify(payload),
    };
    return fetch(
        `/api/referencedata/disposal-recovery-codes/restore-deleted-with-create-values`,
        requestOptions
    ).then(handleResponse) as Promise<IDisposalRecoveryCodeDetail>;
}

function update(payload: DisposalRecoveryCodeUpdatePayload) {
    const requestOptions: RequestInit = {
        method: "POST",
        headers: authHeader(),
        credentials: "include",
        body: JSON.stringify(payload),
    };
    return fetch(`/api/referencedata/disposal-recovery-codes/update`, requestOptions).then(
        handleResponse
    ) as Promise<UpdateMutationResult<IDisposalRecoveryCodeDetail>>;
}

function disable(id: string) {
    return postQuery("disable", id);
}

function enable(id: string) {
    return postQuery("enable", id);
}

function softDelete(id: string) {
    return postQuery("softdelete", id);
}

function restore(id: string) {
    return postQuery("restore", id);
}

function postQuery(action: string, id: string) {
    const requestOptions: RequestInit = {
        method: "POST",
        headers: authHeader(),
        credentials: "include",
    };
    return fetch(
        `/api/referencedata/disposal-recovery-codes/${action}?id=${encodeURIComponent(id)}`,
        requestOptions
    ).then(handleResponse);
}
